import type { DressType } from "@/types/gallery";

export const VALID_DRESS_PRESET_TYPES = [
  "Wedding",
  "Formal",
  "Bridesmaid",
  "Quinceanera",
  "Prom",
  "Modest",
  "Custom",
  "Upload",
] as const;

export type DressPresetType = (typeof VALID_DRESS_PRESET_TYPES)[number];

export function normalizeDressPresetType(
  presetType: unknown
): DressPresetType | null {
  if (typeof presetType !== "string") {
    return null;
  }

  // Fold "ñ"→"n" so legacy tickets stored as "Quinceañera" still resolve now
  // that the canonical spelling is plain-ASCII "Quinceanera".
  const normalized = presetType.trim().toLowerCase().replace(/ñ/g, "n");
  const presetMap: Record<string, DressPresetType> = {
    wedding: "Wedding",
    formal: "Formal",
    bridesmaid: "Bridesmaid",
    quinceanera: "Quinceanera",
    prom: "Prom",
    modest: "Modest",
    custom: "Custom",
    upload: "Upload",
  };

  return presetMap[normalized] || null;
}

/**
 * Maps preset_type from tickets to gallery category tabs
 * @param presetType - The preset_type value from the ticket
 * @returns The corresponding gallery DressType, or "all" if no match
 */
export function mapPresetTypeToGalleryCategory(
  presetType: string | null | undefined
): DressType {
  if (!presetType) {
    return "all";
  }

  const normalizedPresetType = normalizeDressPresetType(presetType);

  if (!normalizedPresetType) {
    return "all";
  }

  // Map preset types to gallery categories
  const presetMap: Record<DressPresetType, DressType> = {
    Prom: "prom",
    Wedding: "wedding",
    Bridesmaid: "bridesmaid",
    Quinceanera: "quinceanera",
    Modest: "modest",
    Formal: "formal",
    Custom: "all",
    Upload: "all",
  };

  return presetMap[normalizedPresetType] || "all";
}

/**
 * Gets all valid preset types that map to a specific gallery category
 * @param category - The gallery category
 * @returns Array of preset_type values that map to this category
 */
export function getPresetTypesForCategory(
  category: Exclude<DressType, "all">
): string[] {
  const categoryMap: Record<Exclude<DressType, "all">, string[]> = {
    prom: ["Prom"],
    wedding: ["Wedding", "Modest"],
    bridesmaid: ["Bridesmaid"],
    // These strings are used verbatim in the DB `preset_type IN (...)` filter.
    // Existing tickets are stored with the accented "Quinceañera" spelling, so
    // both variants must be listed to match legacy rows AND any new plain-ASCII
    // rows. Drop the accented variant once the DB has been migrated (see PR).
    quinceanera: ["Quinceañera", "Quinceanera"],
    modest: ["Modest"],
    formal: ["Formal"],
  };

  return categoryMap[category] || [];
}
